import { motion } from 'motion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGithub, faLinkedin, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { site } from '../data/site';
import Button from '../ui/Button';
import MotionReveal from '../ui/MotionReveal';
import useContactForm from '../hooks/useContactForm';
import { staggerContainer, staggerItem, usePrefersReducedMotion } from '../effects/motionConfig';

const socialIcons = {
  linkedin: faLinkedin,
  github: faGithub,
  x: faXTwitter,
};

function Field({ id, label, children }) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>{label}</label>
      {children}
    </div>
  );
}

function StaggerWrap({ reduced, className, children }) {
  if (reduced) {
    return <div className={className}>{children}</div>;
  }
  return (
    <motion.div className={className} variants={staggerItem}>
      {children}
    </motion.div>
  );
}

export default function ContactSection() {
  const { formRef, sending, notice, sendEmail } = useContactForm();
  const year = new Date().getFullYear();
  const reduced = usePrefersReducedMotion();

  const fields = (
    <>
      <StaggerWrap reduced={reduced} className="contact-section__row">
        <Field id="contact-name" label="Name">
          <input id="contact-name" type="text" name="name" autoComplete="name" required />
        </Field>
        <Field id="contact-email" label="Email">
          <input id="contact-email" type="email" name="email" autoComplete="email" required />
        </Field>
      </StaggerWrap>
      <StaggerWrap reduced={reduced}>
        <Field id="contact-subject" label="Subject">
          <input id="contact-subject" type="text" name="subject" required />
        </Field>
      </StaggerWrap>
      <StaggerWrap reduced={reduced}>
        <Field id="contact-message" label="Message">
          <textarea id="contact-message" name="message" required />
        </Field>
      </StaggerWrap>
      <StaggerWrap reduced={reduced}>
        <Button type="submit" className="btn--primary btn--lg contact-section__submit" disabled={sending}>
          {sending ? (
            <>
              <span className="contact-section__spinner" aria-hidden="true" /> Sending…
            </>
          ) : (
            'Send message'
          )}
        </Button>
      </StaggerWrap>
    </>
  );

  return (
    <section id="contact" className="section contact-section">
      <div className="contact-section__shell">
        <MotionReveal className="contact-section__intro">
          <h2 className="contact-section__headline">Let&apos;s talk.</h2>
          <p className="contact-section__lede">{site.contactIntro}</p>
          <div className="contact-section__social">
            {site.social.map(({ label, href, icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                className="contact-section__social-link"
                aria-label={label}
              >
                <FontAwesomeIcon icon={socialIcons[icon]} />
                <span>{label}</span>
              </a>
            ))}
          </div>
        </MotionReveal>

        <MotionReveal delay={80} className="contact-section__form-wrap">
          {notice.type && (
            <div
              className={`contact-section__notice contact-section__notice--${notice.type}`}
              role={notice.type === 'error' ? 'alert' : 'status'}
              aria-live="polite"
            >
              {notice.text}
            </div>
          )}
          <form ref={formRef} className="contact-section__form" onSubmit={sendEmail} noValidate>
            {reduced ? (
              fields
            ) : (
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15 }}
              >
                {fields}
              </motion.div>
            )}
          </form>
        </MotionReveal>
      </div>

      <footer className="contact-section__footer container">
        &copy; {year} Shah Kabir
      </footer>
    </section>
  );
}
